import React, { useState, useCallback, useEffect, useMemo, Key } from "react";
import Table, {
  ColumnTypes
} from "src/apps/commons/dataDisplays/components/Table";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import useApiAlert from "src/hooks/useApiAlert";
import { ConfirmDialog } from "src/apps/commons/dataDisplays";
import { useDebounce } from "src/utils";
import DataTableToolbar from "./DataTableToolbar";

export interface FilterComponentProps<FilterTypes> {
  open: boolean;
  filter: FilterTypes;
  onSubmit: (filter: FilterTypes) => void;
  onClose: () => void;
}
export interface FilterToolbarProps<FilterTypes> {
  filter: FilterTypes;
  onSubmit: (filter: FilterTypes) => void;
}

interface Props<
  EntityTypes,
  SearchReqTypes,
  OptionKeyTypes,
  AdditionalOptionKeyTypes
> {
  listEntity: (
    req: SearchReqTypes
  ) => Promise<IResponse<IPaginateResponse<EntityTypes>>>;
  deleteEntities?: (req: ICommonIdsReq) => Promise<unknown>;
  onDeleteSuccess?: (req: ICommonIdsReq) => void;
  itemDeletable?: (item: EntityTypes) => boolean;
  itemToString?: (item: EntityTypes) => string;
  filterToolbar?: React.FC<
    FilterToolbarProps<Omit<SearchReqTypes, keyof IPaginateReq<OptionKeyTypes>>>
  >;
  filterDialogCriteriaKeys?: (keyof SearchReqTypes)[];
  filterDialog?: React.FC<
    FilterComponentProps<
      Omit<SearchReqTypes, keyof IPaginateReq<OptionKeyTypes>>
    >
  >;
  defaultCriteria: SearchReqTypes;
  filterValues?: Omit<SearchReqTypes, keyof IPaginateReq<OptionKeyTypes>>;
  columns: ColumnTypes<EntityTypes, OptionKeyTypes, AdditionalOptionKeyTypes>[];
  pathCreate?: string;
  searchTermLabel?: string;
  /** Change this value to force the table to reload data. */
  refreshDataKey?: Key;
}

interface StateTypes<EntityTypes> {
  fetching: boolean;
  datas: EntityTypes[];
  totalCount: number;
  error?: string;
}

export default function DataTable<
  EntityTypes extends { id: string },
  SearchReqTypes extends IPaginateReq<OptionKeyTypes>,
  OptionKeyTypes extends string,
  AdditionalOptionKeyTypes extends string
>(
  props: Props<
    EntityTypes,
    SearchReqTypes,
    OptionKeyTypes,
    AdditionalOptionKeyTypes
  >
) {
  const {
    listEntity,
    deleteEntities,
    onDeleteSuccess,
    itemDeletable,
    itemToString,
    filterToolbar: FilterToolbar,
    filterDialog: FilterDialog,
    filterDialogCriteriaKeys,
    defaultCriteria,
    filterValues,
    columns,
    pathCreate,
    searchTermLabel,
    refreshDataKey
  } = props;

  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();
  const [state, setState] = useState<StateTypes<EntityTypes>>({
    fetching: false,
    datas: [],
    totalCount: 0
  });

  const {
    searchTerm: defaultSearchTerm,
    paginate: defaultPaginate,
    ...restDefaultCriteria
  } = defaultCriteria;

  const [criteria, setCriteria] = useState<IPaginateReq<OptionKeyTypes>>({
    searchTerm: defaultSearchTerm,
    paginate: defaultPaginate
  });
  const [restCriteria, setRestCriteria] = useState<
    Omit<SearchReqTypes, keyof IPaginateReq<OptionKeyTypes>>
  >({ ...restDefaultCriteria });
  const restCriteriaFilterCount = Object.entries(restCriteria).reduce(
    (prevCount, [key, value]) => {
      if (
        filterDialogCriteriaKeys != null &&
        !filterDialogCriteriaKeys.includes(key as keyof SearchReqTypes)
      ) {
        return prevCount;
      }
      if (value != null && value !== "") return prevCount + 1;
      else return prevCount;
    },
    0
  );

  /**
   * TODO: restCriteria in useMemo cause infinite loop
   */
  const { searchTerm, paginate } = criteria;
  const { page, pageSize, order } = paginate;
  const orderDesc = order?.desc;
  const orderBy = order?.optionKey;
  const { value: debouncedSearchTerm, debouncing } = useDebounce(
    searchTerm,
    800
  );

  const debouncedCriteria = useMemo(
    () => ({
      paginate: {
        page,
        pageSize,
        order
      },
      searchTerm: debouncedSearchTerm,
      ...restCriteria,
      ...filterValues
    }),
    [page, pageSize, order, debouncedSearchTerm, restCriteria, filterValues]
  );

  const fetchList = useCallback(async () => {
    setState(s => ({ ...s, fetching: true }));
    try {
      const resp = await listEntity(debouncedCriteria as SearchReqTypes);
      const { results, totalCount } = resp.value;
      setState({ fetching: false, datas: results, totalCount: totalCount });
    } catch (e) {
      handleError(e);
      setState({
        fetching: false,
        datas: [],
        totalCount: 0,
        error: e.message
      });
    }
  }, [listEntity, handleError, debouncedCriteria]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshDataKey]);

  const handleChangePageProps = (
    changedPage: number,
    changedPageSize: number
  ) => {
    setCriteria(s => ({
      ...s,
      paginate: { ...s.paginate, page: changedPage, pageSize: changedPageSize }
    }));
  };

  const handleSort = (key: OptionKeyTypes) => {
    const toggleDesc = order?.optionKey === key ? !order?.desc : false;
    setCriteria(d => ({
      ...d,
      paginate: {
        ...d.paginate,
        order: { optionKey: key, desc: toggleDesc }
      }
    }));
  };

  const handleChangeSearchTerm = (changedSearchTerm?: string) => {
    setCriteria(c => ({ ...c, searchTerm: changedSearchTerm }));
  };

  /**
   * Filter
   */
  const [openFilter, setOpenFilter] = useState(false);
  const handleOpenFilter = FilterDialog
    ? () => {
        setOpenFilter(true);
      }
    : undefined;
  const handleCloseFilter = () => {
    setOpenFilter(false);
  };
  const handleSubmitFilter = (
    filter: Omit<SearchReqTypes, keyof IPaginateReq<OptionKeyTypes>>
  ) => {
    setRestCriteria(filter);
    setOpenFilter(false);
  };

  const handleSubmitFilterToolbar = (
    filter: Omit<SearchReqTypes, keyof IPaginateReq<OptionKeyTypes>>
  ) => {
    setRestCriteria(filter);
  };

  /**
   * Select & Delete
   */
  const selectable = Boolean(deleteEntities);
  const [selectedItems, setSelectedItems] = useState<EntityTypes[]>([]);
  const itemSelected = useCallback(
    (item: EntityTypes) => selectedItems.some(it => it.id === item.id),
    [selectedItems]
  );
  const onSelectionChanged = useCallback(
    (item: EntityTypes, selected: boolean) => {
      if (selected) {
        setSelectedItems([
          ...selectedItems.filter(it => it.id !== item.id),
          item
        ]);
      } else {
        setSelectedItems(selectedItems.filter(it => it.id !== item.id));
      }
    },
    [selectedItems]
  );

  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setDeleteModalOpen(false), []);
  const confirmDelete = useCallback(async () => {
    if (!deleteEntities) return;
    setDeleting(true);
    try {
      const req = { ids: selectedItems.map(it => it.id) };
      await deleteEntities(req);
      setMessage("success", `ลบ ${selectedItems.length} รายการ สำเร็จ`);
      setSelectedItems([]);
      onDeleteSuccess?.(req);
      fetchList();
    } catch (e) {
      handleError(e);
    } finally {
      setDeleting(false);
      closeDeleteModal();
    }
  }, [
    closeDeleteModal,
    deleteEntities,
    onDeleteSuccess,
    fetchList,
    handleError,
    selectedItems,
    setMessage
  ]);
  const deleteDescription = useCallback(() => {
    if (!itemToString) return undefined;
    return selectedItems.map(it => itemToString(it)).join("\n");
  }, [selectedItems, itemToString]);

  const isLoading = state.fetching || debouncing;

  return (
    <>
      <DataTableToolbar
        onChangeSearchTerm={handleChangeSearchTerm}
        handleOpenFilter={handleOpenFilter}
        pathCreate={pathCreate}
        searchTermLabel={searchTermLabel}
        activeFilterCount={restCriteriaFilterCount}
        handleClickDelete={selectable ? openDeleteModal : undefined}
        deletingCount={selectable ? selectedItems.length : undefined}
      >
        {FilterToolbar && (
          <FilterToolbar
            filter={restCriteria}
            onSubmit={handleSubmitFilterToolbar}
          />
        )}
      </DataTableToolbar>

      <Table<EntityTypes, OptionKeyTypes, AdditionalOptionKeyTypes>
        searchHeight={80}
        isLoading={isLoading}
        columns={columns}
        datas={state.datas}
        page={page}
        pageSize={pageSize}
        totalCount={state.totalCount}
        orderDesc={orderDesc}
        orderBy={orderBy}
        handleChangePageProps={handleChangePageProps}
        handleSort={handleSort}
        selectable={selectable}
        itemSelected={itemSelected}
        itemSelectable={itemDeletable}
        onSelectionChanged={onSelectionChanged}
      />

      {FilterDialog && (
        <FilterDialog
          key="filter_dialog"
          open={openFilter}
          filter={restCriteria}
          onSubmit={handleSubmitFilter}
          onClose={handleCloseFilter}
        />
      )}

      {deleteEntities && (
        <ConfirmDialog
          title={`ยืนยันการลบ ${selectedItems.length} รายการที่เลือก`}
          descriptionBuilder={itemToString ? deleteDescription : undefined}
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          confirming={deleting}
        />
      )}
    </>
  );
}
